import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLogoTheme } from '../context/LogoThemeContext';
import { fetchSingleItem } from '../lib/cms';

export default function Footer() {
  const { currentPreset } = useLogoTheme();
  const [kontaktData, setKontaktData] = useState(null);

  useEffect(() => {
    async function loadContact() {
      const data = await fetchSingleItem('kontakt_oplysninger');
      if (data) setKontaktData(data);
    }
    loadContact();
  }, []);

  const footerText = kontaktData?.om_tekst_footer || 'Et trygt rum uden præstation, hvor der er plads til hele dig. Hvor hjertet taler, og dit hjerte bliver hørt.';
  const adresseVej = kontaktData?.adresse || 'Ganløsevej 38';
  const adresseBy = kontaktData?.postnr_by || '3520 Farum';
  const telefon = kontaktData?.telefon || '24 91 43 13';
  const telefonClean = telefon.replace(/\s+/g, '');
  const email = kontaktData?.email || 'kontakt@leneaevolinee.dk';
  const cvr = kontaktData?.cvr || '31679478';

  return (
    <footer className="bg-[#2B2725] text-[#FAF7F2] pt-16 pb-12 border-t border-[#4A423D]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-[#4A423D]">
          {/* Brand Info */}
          <div className="space-y-4">
            <Link to="/" className="inline-block group focus:outline-hidden">
              <div className="bg-[#DFD9CD] px-5 py-3.5 rounded-xl inline-block transition-transform duration-200 group-hover:scale-105 shadow-sm">
                <img
                  src={currentPreset?.url || "https://assets.auxodev.dk/assets/40e16184-d312-45e0-99c1-a83e20a53a01/brown-minimalist-typographic-candle-business-logo4.jpg"}
                  alt="Lenea Evolinee"
                  className="h-16 sm:h-20 w-auto object-contain"
                />
              </div>
            </Link>
            <p className="text-xs text-[#D8CDBF] leading-relaxed max-w-xs font-sans">
              {footerText}
            </p>
          </div>

          {/* Navigation Links */}
          <div className="space-y-3">
            <h3 className="font-serif text-xl font-medium text-white tracking-wide">
              Navigation
            </h3>
            <ul className="space-y-2 text-sm text-[#D8CDBF]">
              <li>
                <Link to="/" className="hover:text-white transition-colors cursor-pointer">
                  Forside
                </Link>
              </li>
              <li>
                <Link to="/om-zentai" className="hover:text-white transition-colors cursor-pointer">
                  Om Lenea Evolinee
                </Link>
              </li>
              <li>
                <Link to="/forlob" className="hover:text-white transition-colors cursor-pointer">
                  Forløb & Fællesskaber
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="hover:text-white transition-colors cursor-pointer">
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>

          {/* Forløb Links */}
          <div className="space-y-3">
            <h3 className="font-serif text-xl font-medium text-white tracking-wide">
              Forløb
            </h3>
            <ul className="space-y-2 text-sm text-[#D8CDBF]">
              <li>
                <Link to="/forbundet-fra-hjertet" className="hover:text-white transition-colors cursor-pointer">
                  Forbundet fra Hjertet
                </Link>
              </li>
              <li>
                <Link to="/1-1-forlob" className="hover:text-white transition-colors cursor-pointer">
                  1:1-forløb
                </Link>
              </li>
              <li>
                <Link to="/lyt-fra-hjertet" className="hover:text-white transition-colors cursor-pointer">
                  Lyt – fra Hjertet
                </Link>
              </li>
              <li>
                <Link to="/forbundet-med-dit-horoskop" className="hover:text-white transition-colors cursor-pointer">
                  Forbundet med dit horoskop (1:1)
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Details */}
          <div className="space-y-3">
            <h3 className="font-serif text-xl font-medium text-white tracking-wide">
              Kontakt & Klinik
            </h3>
            <div className="space-y-2.5 text-sm text-[#D8CDBF]">
              <p className="flex items-start gap-2.5">
                <i className="fa-solid fa-location-dot mt-1 text-[#D8ADA0]" aria-hidden="true" />
                <span>
                  {adresseVej}<br />
                  {adresseBy}
                </span>
              </p>
              <p className="flex items-center gap-2.5">
                <i className="fa-solid fa-phone text-[#D8ADA0]" aria-hidden="true" />
                <a href={`tel:${telefonClean}`} className="hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                  {telefon}
                </a>
              </p>
              <p className="flex items-center gap-2.5">
                <i className="fa-solid fa-envelope text-[#D8ADA0]" aria-hidden="true" />
                <a href={`mailto:${email}`} className="hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                  {email}
                </a>
              </p>
              <p className="flex items-center gap-2.5 pt-1 text-xs text-[#A89A8B]">
                <i className="fa-solid fa-id-card" aria-hidden="true" />
                <span>CVR: {cvr}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Bottom copyright & Socials */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-[#A89A8B] gap-4">
          <p>© {new Date().getFullYear()} Lenea Evolinee. Alle rettigheder forbeholdes.</p>
          <div className="flex items-center space-x-4">
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="hover:text-white transition-colors text-base"
            >
              <i className="fa-brands fa-instagram" aria-hidden="true" />
            </a>
            <a
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="hover:text-white transition-colors text-base"
            >
              <i className="fa-brands fa-facebook" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
